import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import { notesAPI, NOTE_TYPES, NOTE_COLORS, formatDate } from '../utils/api';
import './NoteEditor.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean'],
  ],
};
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
  'blockquote', 'code-block', 'link', 'image',
];

export default function NoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('note');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [color, setColor] = useState(NOTE_COLORS[0]);
  const [isPinned, setIsPinned] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [noteData, setNoteData] = useState(null);
  //const [showColorPicker, setShowColorPicker] = useState(false);
  const autoSaveRef = useRef(null);

  // Load note if editing
  useEffect(() => {
    if (id) {
      notesAPI.getOne(id)
        .then(res => {
          const n = res.data.note;
          setNoteData(n);
          setTitle(n.title);
          setContent(n.content);
          setType(n.type);
          setTags(n.tags);
          setColor(n.color);
          setIsPinned(n.isPinned);
          setImages(n.images || []);
        })
        .catch(() => { setError('Note not found'); })
        .finally(() => setLoading(false));
    }
  }, [id]);

  // Auto-save (debounced, every 3s after change)
  useEffect(() => {
    if (isNew || loading) return;
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => { handleSave(true); }, 3000);
    return () => clearTimeout(autoSaveRef.current);
  // eslint-disable-next-line
  }, [title, content, tags, type, color, isPinned]);

  const handleSave = async (isAuto = false) => {
    if (!title && !content) return;
    setSaving(true);
    setError('');
    try {
      const payload = { title: title || 'Untitled Note', content, type, tags, color, isPinned, images };
      if (isNew) {
        const res = await notesAPI.create(payload);
        navigate(`/note/${res.data.note._id}`, { replace: true });
      } else {
        await notesAPI.update(id, payload);
      }
      if (!isAuto) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase().replace(/[^a-z0-9-_]/g, '');
      if (newTag && !tags.includes(newTag) && tags.length < 10) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
    }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImages(prev => [...prev, {
          data: ev.target.result,
          name: file.name,
          mimeType: file.type,
          size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="editor-loading">
        <div className="spinner" style={{ width: 36, height: 36 }} />
      </div>
    );
  }

  return (
    <div className="editor-page">
      {/* ── Editor Topbar ── */}
      <header className="editor-topbar">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/dashboard')}>
          ← Back
        </button>
        <div className="editor-meta">
          {noteData && <span className="editor-date">{formatDate(noteData.updatedAt)}</span>}
          {saving && <span className="save-status">💾 Saving...</span>}
          {saved && <span className="save-status save-ok">✓ Saved</span>}
          {error && <span className="save-status save-err">⚠ {error}</span>}
        </div>
        <div className="editor-actions">
          <button className={`btn btn-ghost btn-sm ${isPinned ? 'pinned' : ''}`} onClick={() => setIsPinned(p => !p)}>
            {isPinned ? '📌 Pinned' : '📌 Pin'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? '...' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </header>

      <div className="editor-body">
        {/* ── Sidebar Controls ── */}
        <aside className="editor-sidebar">
          <div className="control-section">
            <label className="control-label">TYPE</label>
            <div className="type-pills">
              {NOTE_TYPES.map(t => (
                <button key={t.value}
                  className={`type-pill ${type === t.value ? 'active' : ''}`}
                  onClick={() => setType(t.value)}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="control-section">
            <label className="control-label">TAGS</label>
            <div className="tags-input-wrapper">
              {tags.map(t => (
                <span key={t} className="tag">
                  # {t}
                  <button onClick={() => setTags(tags.filter(x => x !== t))}>✕</button>
                </span>
              ))}
              <input
                className="tag-input"
                placeholder="Add tag…"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
              />
            </div>
            <p className="hint">Press Enter or comma to add</p>
          </div>

          <div className="control-section">
            <label className="control-label">CARD COLOR</label>
            <div className="color-picker">
              {NOTE_COLORS.map(c => (
                <button key={c} className={`color-swatch ${color === c ? 'active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)} />
              ))}
            </div>
          </div>

          <div className="control-section">
            <label className="control-label">IMAGES</label>
            <label className="upload-btn">
              📎 Attach Image
              <input type="file" accept="image/*" multiple hidden onChange={handleImageUpload} />
            </label>
            {images.length > 0 && (
              <div className="image-thumbs">
                {images.map((img, i) => (
                  <div key={i} className="img-thumb">
                    <img src={img.data} alt={img.name} />
                    <button onClick={() => setImages(images.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {noteData && (
            <div className="control-section note-info">
              <div className="info-row"><span>Words</span><span>{noteData.wordCount}</span></div>
              <div className="info-row"><span>Created</span><span>{formatDate(noteData.createdAt)}</span></div>
            </div>
          )}
        </aside>

        {/* ── Editor Main ── */}
        <div className="editor-main">
          <input
            className="title-input"
            placeholder="Note title…"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={200}
          />
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Start writing…"
            className="quill-editor"
          />
        </div>
      </div>
    </div>
  );
}
